import "./globals.css";
import "@modes0/ui-components/styles.css";
import type { AppProps } from "next/app";
import Head from "next/head";
import Link from "next/link";
import "react-image-gallery/styles/css/image-gallery.css";
import Script from "next/script";
import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import type { ComponentProps, MouseEvent } from "react";
import { initGsapAnimations } from "@/lib/animations/gsapInit";
import { Footer as Modes0Footer } from "@modes0/ui-components";
import {
  GoogleReviewsSticky,
  MegaNav,
  StickyContact,
} from "@modes0/ui-components/client";
import {
  BUSINESS_NAME,
  WEBSITE_URL,
  businessDetails,
  contactDetails as siteContactDetails,
  footerData,
  googleDetails,
  headerNavMenuData,
  socialMediaProfiles,
} from "@/content";
import ServiceMegaNavSearch from "@/ui/header-navigation/service-mega-nav-search";
import {MetaTags} from '@modes0/ui-components-seo';
import {seoSiteProfile} from '@/content/seo';

const GA_MEASUREMENT_ID = "G-DSN5ESMGHX";
const BRAND_BLUE = "#28aae1";
const BRAND_ORANGE = "#f68e22";
const BRAND_NAVY = "#15171e";
const UTILITY_BAR_CLASS = "bg-[#15171e]";

const socialLinks = [
  socialMediaProfiles.facebookHandle && {
    label: "Facebook",
    href: `${socialMediaProfiles.facebookLink}${socialMediaProfiles.facebookHandle}`,
    icon: "facebook",
    external: true,
    ariaLabel: `${BUSINESS_NAME} on Facebook`,
  },
  socialMediaProfiles.instaHandle && {
    label: "Instagram",
    href: `${socialMediaProfiles.instaLink}${socialMediaProfiles.instaHandle}`,
    icon: "instagram",
    external: true,
    ariaLabel: `${BUSINESS_NAME} on Instagram`,
  },
  socialMediaProfiles.linkedInHandle && {
    label: "LinkedIn",
    href: `${socialMediaProfiles.linkedInLink}${socialMediaProfiles.linkedInHandle}`,
    icon: "linkedin",
    external: true,
    ariaLabel: `${BUSINESS_NAME} on LinkedIn`,
  },
].filter(Boolean);

const footerBusinessDetails = {
  ...businessDetails,
  companyData: {
    ...businessDetails.companyData,
    licenseNumber: businessDetails.companyData?.abn
      ? `ABN ${businessDetails.companyData.abn}`
      : businessDetails.companyData?.licenseNumber,
  },
};

export default function MyApp({ Component, pageProps, router }: AppProps) {
  const [navResetKey, setNavResetKey] = useState(0);
  const currentPageUrl = `${WEBSITE_URL}${router.asPath}`;

  const handleNavLinkClick = useCallback((event: MouseEvent, href: unknown) => {
    if (
      event.defaultPrevented ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      (typeof event.button === "number" && event.button !== 0)
    ) {
      return;
    }

    const hrefValue =
      typeof href === "string"
        ? href
        : (href as { pathname?: string } | undefined)?.pathname;

    if (
      !hrefValue ||
      hrefValue.startsWith("#") ||
      hrefValue.startsWith("tel:") ||
      hrefValue.startsWith("mailto:") ||
      hrefValue.startsWith("http")
    ) {
      return;
    }

    setNavResetKey((key) => key + 1);
  }, []);

  const MegaNavLink = useMemo(() => {
    const Component = forwardRef<
      HTMLAnchorElement,
      ComponentProps<typeof Link>
    >(({ onClick, href, className, style, ...props }, ref) => {
      const classNameValue =
        typeof className === "string" ? className : undefined;
      const isMegaNavCta = classNameValue?.includes("inline-flex h-9");
      const isMegaNavDesktopTopLink = classNameValue?.includes(
        "flex h-full min-w-0 items-center",
      );
      const resolvedClassName = classNameValue
        ? [
            classNameValue,
            isMegaNavDesktopTopLink ? "ozy-meganav-top-link" : "",
            isMegaNavCta ? "ozy-meganav-cta" : "",
          ]
            .filter(Boolean)
            .join(" ")
        : className;

      return (
        <Link
          ref={ref}
          href={href}
          className={resolvedClassName}
          style={{
            ...style,
            ...(isMegaNavDesktopTopLink
              ? {
                  fontSize: "15px",
                  fontWeight: 800,
                  lineHeight: 1.1,
                }
              : {}),
          }}
          {...props}
          onClick={(event) => {
            onClick?.(event);
            handleNavLinkClick(event, href);
          }}
        />
      );
    });

    Component.displayName = "MegaNavLink";
    return Component;
  }, [handleNavLinkClick]);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      if (window.gtag) {
        window.gtag("config", GA_MEASUREMENT_ID, {
          page_path: url,
        });
      }
    };
    router.events.on("routeChangeComplete", handleRouteChange);
    return () => router.events.off("routeChangeComplete", handleRouteChange);
  }, [router.events]);

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    const handleRouteChange = async () => {
      if (cleanup) cleanup();
      cleanup = await initGsapAnimations();
    };

    handleRouteChange();
    router.events.on("routeChangeComplete", handleRouteChange);

    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
      if (cleanup) cleanup();
    };
  }, [router.events]);

  return (
    <>
      <Head>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1"
          key="viewport"
        />
      </Head>
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
        strategy="afterInteractive"
      />
      <Script id="gtag-init" strategy="afterInteractive">
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${GA_MEASUREMENT_ID}', {
            page_path: window.location.pathname,
          });
        `}
      </Script>

      <div className="ozy-meganav">
        <MegaNav
          key={`${router.asPath}-${navResetKey}`}
          BUSINESS_NAME={BUSINESS_NAME}
          businessDetails={businessDetails as never}
          Link={MegaNavLink as never}
          headerNavMenuData={headerNavMenuData as never}
          currentPathname={router.asPath}
          theme="light"
          layout="xl"
          position="sticky"
          contactDetails={{
            phone: siteContactDetails.contactNumberOne,
            phoneLabel: siteContactDetails.contactNumberOne,
            email: siteContactDetails.contactEmail,
            emailLabel: siteContactDetails.contactEmail,
          }}
          socialLinks={socialLinks as never}
          cta={{ label: "Contact us", href: "/contact" }}
          renderSearch={(props) => <ServiceMegaNavSearch {...props} />}
          topLevelActiveColor={BRAND_ORANGE}
          topLevelActiveStyle="underline"
          topLevelLinkStyleOverrides={{
            link: { color: BRAND_NAVY },
            label: { color: "inherit" },
            activeUnderline: {
              borderBottomColor: BRAND_ORANGE,
              color: BRAND_ORANGE,
            },
            activeBackground: {
              backgroundColor: BRAND_ORANGE,
              color: "#ffffff",
              boxShadow: `inset 0 0 0 1px ${BRAND_ORANGE}`,
            },
          }}
          utilityDisplayMode="icon-label"
          utilityBarBackgroundClassName={UTILITY_BAR_CLASS}
          utilityBarTextColor="white"
          hideUtilityBarOnScroll
          transparentBackground={{
            enabled: true,
            mobile: true,
            desktop: true,
            scrollThreshold: 12,
            topClassName: "bg-white/75 backdrop-blur-[6px]",
            scrolledClassName: "bg-white/92 backdrop-blur-[3px]",
            borderClassName: "border-b border-[#28aae1]/20",
            scrolledBorderClassName: "border-b border-[#15171e]/10",
            utilityBarTopClassName: UTILITY_BAR_CLASS,
            utilityBarScrolledClassName: UTILITY_BAR_CLASS,
          }}
        />
      </div>
      <main>
        <MetaTags
                    meta={seoSiteProfile.defaultMeta}
                    siteProfile={seoSiteProfile}
                    includeWebPageSchema={false}
                />
        <Component {...pageProps} />
      </main>
      <Modes0Footer
        Link={Link as never}
        businessName={BUSINESS_NAME}
        businessDetails={footerBusinessDetails as never}
        footerData={footerData as never}
        socialMediaProfiles={socialMediaProfiles}
        googleDetails={googleDetails}
        hideSocialBlock={!socialLinks.length}
        theme="dark"
        layout="xl"
      />
      {googleDetails.googleReviewLink ? (
        <GoogleReviewsSticky
          googleDetails={googleDetails}
          title={`${BUSINESS_NAME} on Google`}
          rating={5}
          reviewCountText="Customer reviews"
          linkLabel="Leave a review"
          reviewsLink={googleDetails.googleReviewLink}
          position="bottom-left"
          accentColor={BRAND_BLUE}
          readReviewsColor={BRAND_NAVY}
          opacity={0.97}
        />
      ) : null}
      <StickyContact
        contactDetails={siteContactDetails}
        businessName={BUSINESS_NAME}
        pageUrl={currentPageUrl}
        title={`Contact ${BUSINESS_NAME}`}
        description="Call, SMS or email Ozy Wide Group about CCTV, alarms, access control, guards, patrols and security support across Sydney."
        methods={["call", "sms", "email"]}
        labels={{
          callTitle: `Call ${BUSINESS_NAME}`,
          smsTitle: `SMS ${BUSINESS_NAME}`,
          emailTitle: `Email ${BUSINESS_NAME}`,
        }}
        colors={{
          accent: BRAND_BLUE,
          call: BRAND_NAVY,
          sms: BRAND_ORANGE,
          email: BRAND_BLUE,
          background: "#ffffff",
          surface: "#edf7fc",
          text: BRAND_NAVY,
          mutedText: "rgba(21, 23, 30, 0.68)",
          border: "rgba(40, 170, 225, 0.2)",
          shadow: "0 16px 44px rgba(21, 23, 30, 0.18)",
        }}
        position="bottom-right"
        opacity={0.98}
      />
    </>
  );
}
